import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  saveTimesheet,
  getTimesheetsByUser,
  validateByForeman,
  validateByHr,
  removeTimesheet,
  editTimesheet,
  getAllTimesheets,
  getTimesheetsByForeman,
  getTimesheetsByHR,
  purgeOldTimesheets,
} from '../services/api';
import { RootState } from './index';
import { getISOWeek } from 'date-fns';
import { FetchTimesheetsParams, Timesheet, TimesheetState } from '../interfaces/timesheet';
import toast from 'react-hot-toast';

const initialState: TimesheetState = {
  items: [],
  pendingItems: [],
  loading: false,
  error: null
};

// Async thunks
export const fetchTimesheetsByUser = createAsyncThunk(
  'timesheets/fetchTimesheetsByUser',
  async (params: FetchTimesheetsParams, { getState }) => {
    try {
      const response = await getTimesheetsByUser(params?.weekNumber, params?.year);
      // Transform the API response to match our Timesheet interface
      const transformedData: Timesheet[] = response.data?.map((item: any) => ({
        id: item.id,
        chefChantier: {
          name: item.chefChantier,
          matricule: item.matricule,
          email: null
        }
        ,
        chefProjet: {
          name: item.chefProjet,
          matricule: null,
          email: null
        },
        codeSalaire: item.codeSalaire,
        travailleur: {
          name: item.travailleur,
          matricule: item.matricule,
          isInterim: item.isInterim,
          interimCompanyName: item.interimCompanyName,
        },
        worksite: {
          name: item.worksite,
          matricule: null
        },
        remark: item.remark,
        isInterim: item.isInterim, // Assuming isInterim is stored as 1 or 0 in the database
        interimCompanyName: item.interimCompanyName,
        foreman_validated: Boolean(item.foreman_validated),
        hr_validated: Boolean(item.hr_validated),
        foreman_validator: item.foreman_validator,
        foreman_validation_date: item.foreman_validation_date,
        hr_validator: item.hr_validator,
        hr_validation_date: item.hr_validation_date,
        created_at: item.created_at,
        created_by: item.created_by,
        weekNumber: item.weekNumber,
        timesheetDate: item.timesheetDate,
        localId: item.localId || undefined,
        syncStatus: 'synced',
        _pendingDelete: item._pendingDelete || false,
        _pendingUpdate: item._pendingUpdate || false,
        dayDetails: {
          lundi: item.dayDetails.lundi,
          mardi: item.dayDetails.mardi,
          mercredi: item.dayDetails.mercredi,
          jeudi: item.dayDetails.jeudi,
          vendredi: item.dayDetails.vendredi,
          samedi: item.dayDetails.samedi,
          dimanche: item.dayDetails.dimanche
        },
      }));
      return transformedData;
    } catch (error) {
      toast.error("Error fetching timesheets: " + error)
      // Return current state items instead of failing completely
      const state = getState() as RootState;
      return state.timesheets.items
    }
  }
);

export const fetchTimesheetsByForeman = createAsyncThunk(
  'timesheets/fetchTimesheetsByForeman',
  async (params: FetchTimesheetsParams, { getState }) => {
    try {
      const response = await getTimesheetsByForeman(params?.weekNumber, params?.year);
      // Transform the API response to match our Timesheet interface
      const transformedData: Timesheet[] = response.data.map((item: any) => ({
        id: item.id,
        chefChantier: {
          name: item.chefChantier,
          matricule: null,
          email: null
        }
        ,
        chefProjet: {
          name: item.chefProjet,
          matricule: null,
          email: null
        },
        codeSalaire: item.codeSalaire,
        travailleur: {
          name: item.travailleur,
          matricule: item.matricule,
          isInterim: item.isInterim,
          interimCompanyName: item.interimCompanyName,

        },
        worksite: {
          name: item.worksite,
          matricule: null
        },
        remark: item.remark,
        isInterim: item.isInterim, // Assuming isInterim is stored as 1 or 0 in the database
        interimCompanyName: item.interimCompanyName,
        foreman_validated: Boolean(item.foreman_validated),
        hr_validated: Boolean(item.hr_validated),
        foreman_validator: item.foreman_validator,
        foreman_validation_date: item.foreman_validation_date,
        hr_validator: item.hr_validator,
        hr_validation_date: item.hr_validation_date,
        created_at: item.created_at,
        created_by: item.created_by,
        weekNumber: item.weekNumber,
        timesheetDate: item.timesheetDate,
        localId: item.localId || undefined,
        syncStatus: 'synced',
        _pendingDelete: item._pendingDelete || false,
        _pendingUpdate: item._pendingUpdate || false,
        dayDetails: {
          lundi: item.dayDetails.lundi,
          mardi: item.dayDetails.mardi,
          mercredi: item.dayDetails.mercredi,
          jeudi: item.dayDetails.jeudi,
          vendredi: item.dayDetails.vendredi,
          samedi: item.dayDetails.samedi,
          dimanche: item.dayDetails.dimanche
        },
      }));
      return transformedData;
    } catch (error) {
      // Return current state items instead of failing completely
      const state = getState() as RootState;
      return state.timesheets.items
    }
  }
);

export const fetchTimesheetsByHR = createAsyncThunk(
  'timesheets/fetchTimesheetsByHR',
  async (params: FetchTimesheetsParams, { getState }) => {
    try {
      const response = await getTimesheetsByHR(params?.weekNumber, params?.year);
      // Transform the API response to match our Timesheet interface
      const transformedData: Timesheet[] = response.data.map((item: any) => ({
        id: item.id,
        chefChantier: {
          name: item.chefChantier,
          matricule: null,
          email: null
        }
        ,
        chefProjet: {
          name: item.chefProjet,
          matricule: null,
          email: null
        },
        codeSalaire: item.codeSalaire,
        travailleur: {
          name: item.travailleur,
          matricule: item.matricule,
          isInterim: item.isInterim,
          interimCompanyName: item.interimCompanyName,

        },
        worksite: {
          name: item.worksite,
          matricule: null
        },
        remark: item.remark,
        isInterim: item.isInterim, // Assuming isInterim is stored as 1 or 0 in the database
        interimCompanyName: item.interimCompanyName,
        foreman_validated: Boolean(item.foreman_validated),
        hr_validated: Boolean(item.hr_validated),
        foreman_validator: item.foreman_validator,
        foreman_validation_date: item.foreman_validation_date,
        hr_validator: item.hr_validator,
        hr_validation_date: item.hr_validation_date,
        created_at: item.created_at,
        created_by: item.created_by,
        weekNumber: item.weekNumber,
        timesheetDate: item.timesheetDate,
        localId: item.localId || undefined,
        syncStatus: 'synced',
        _pendingDelete: item._pendingDelete || false,
        _pendingUpdate: item._pendingUpdate || false,
        dayDetails: {
          lundi: item.dayDetails.lundi,
          mardi: item.dayDetails.mardi,
          mercredi: item.dayDetails.mercredi,
          jeudi: item.dayDetails.jeudi,
          vendredi: item.dayDetails.vendredi,
          samedi: item.dayDetails.samedi,
          dimanche: item.dayDetails.dimanche
        },
      }));
      return transformedData;
    } catch (error) {
      // Return current state items instead of failing completely
      const state = getState() as RootState;
      return state.timesheets.items;
    }
  }
);

export const fetchAllTimesheets = createAsyncThunk(
  'timesheets/fetchaLLTimesheets',
  async (params: FetchTimesheetsParams, { getState }) => {
    try {
      const response = await getAllTimesheets(params?.weekNumber, params?.year);
      // Transform the API response to match our Timesheet interface
      const transformedData: Timesheet[] = response.data.map((item: any) => ({
        id: item.id,
        chefChantier: {
          name: item.chefChantier,
          matricule: null,
          email: null
        }
        ,
        chefProjet: {
          name: item.chefProjet,
          matricule: null,
          email: null
        },
        codeSalaire: item.codeSalaire,
        travailleur: {
          name: item.travailleur,
          matricule: item.matricule,
          isInterim: item.isInterim,
          interimCompanyName: item.interimCompanyName,

        },
        worksite: {
          name: item.worksite,
          matricule: null
        },
        remark: item.remark,
        isInterim: item.isInterim, // Assuming isInterim is stored as 1 or 0 in the database
        interimCompanyName: item.interimCompanyName,
        foreman_validated: Boolean(item.foreman_validated),
        hr_validated: Boolean(item.hr_validated),
        foreman_validator: item.foreman_validator,
        foreman_validation_date: item.foreman_validation_date,
        hr_validator: item.hr_validator,
        hr_validation_date: item.hr_validation_date,
        created_at: item.created_at,
        created_by: item.created_by,
        weekNumber: item.weekNumber,
        timesheetDate: item.timesheetDate,
        localId: item.localId || undefined,
        syncStatus: 'synced',
        _pendingDelete: item._pendingDelete || false,
        _pendingUpdate: item._pendingUpdate || false,
        dayDetails: {
          lundi: item.dayDetails.lundi,
          mardi: item.dayDetails.mardi,
          mercredi: item.dayDetails.mercredi,
          jeudi: item.dayDetails.jeudi,
          vendredi: item.dayDetails.vendredi,
          samedi: item.dayDetails.samedi,
          dimanche: item.dayDetails.dimanche
        },
      }));

      return transformedData;
    } catch (error) {
      // Return current state items instead of failing completely
      const state = getState() as RootState;
      return state.timesheets.items;
    }
  }
);

export const createTimesheet = createAsyncThunk<
  { timesheet: Timesheet; },
  Timesheet,
  {
    rejectValue: string;
  }
>(
  'timesheets/createTimesheet',
  async (timesheet, { rejectWithValue, dispatch }) => {
    try {
      const response = await saveTimesheet({
        chefChantier: timesheet.chefChantier.name,
        chefProjet: timesheet.chefProjet.name,
        travailleur: timesheet.travailleur.name,
        weekNumber: timesheet.weekNumber,
        timesheetDate: timesheet.timesheetDate,
        worksite: timesheet.worksite.name,
        remark: timesheet.remark,
        isInterim: timesheet.isInterim,
        interimCompanyName: timesheet.interimCompanyName,
        dayDetails: {
          lundi: timesheet.dayDetails.lundi,
          mardi: timesheet.dayDetails.mardi,
          mercredi: timesheet.dayDetails.mercredi,
          jeudi: timesheet.dayDetails.jeudi,
          vendredi: timesheet.dayDetails.vendredi,
          samedi: timesheet.dayDetails.samedi,
          dimanche: timesheet.dayDetails.dimanche
        }
      });

      // If online, fetch the latest data after successful creation
      setTimeout(async () => {
        await dispatch(fetchTimesheetsByUser({ weekNumber: getISOWeek(new Date()), year: new Date().getFullYear() })).unwrap();
      }, 500);

      // Transform online response to match the expected return type
      return {
        timesheet: response.data.timesheet || {
          id: response.data.timesheetId,
          chefChantier: timesheet.chefChantier,
          chefProjet: timesheet.chefProjet,
          travailleur: timesheet.travailleur,
          weekNumber: timesheet.weekNumber,
          timesheetDate: timesheet.timesheetDate,
          worksite: timesheet.worksite,
          syncStatus: 'synced',
          created_at: response.data.updatedAt || new Date().toISOString(),
          remark: timesheet.remark,
          isInterim: timesheet.isInterim,
          interimCompanyName: timesheet.interimCompanyName,
          dayDetails: {
            lundi: timesheet.dayDetails.lundi,
            mardi: timesheet.dayDetails.mardi,
            mercredi: timesheet.dayDetails.mercredi,
            jeudi: timesheet.dayDetails.jeudi,
            vendredi: timesheet.dayDetails.vendredi,
            samedi: timesheet.dayDetails.samedi,
            dimanche: timesheet.dayDetails.dimanche
          }
        }
      };
    } catch (error) {
      return rejectWithValue('Erreur lors de la création');
    }
  }
);

// Update updateTimesheet to refresh list after update
export const updateTimesheet = createAsyncThunk<
  { timesheet: Timesheet; },
  Timesheet,
  {
    rejectValue: string;
  }
>(
  'timesheets/updateTimesheet',
  async (timesheet: Timesheet, { getState, rejectWithValue, dispatch }) => {
    try {
      if (!timesheet.id) {
        // update the state of pending items and update the changed element
        const state = getState() as RootState;
        let pendingItem = state.timesheets.pendingItems.find(item => item.localId === timesheet.localId);
        if (pendingItem !== undefined) {
          // update state.timesheets.pendingItems
          return { timesheet };
        }

      }

      // Only update timesheets that have a server ID
      const response = await editTimesheet({
        ...timesheet,
        id: timesheet.id,
        chefChantier: timesheet.chefChantier.name,
        chefProjet: timesheet.chefProjet.name,
        travailleur: timesheet.travailleur.name,
        weekNumber: timesheet.weekNumber,
        timesheetDate: timesheet.timesheetDate,
        worksite: timesheet.worksite.name,
        remark: timesheet.remark,
        isInterim: timesheet.isInterim,
        interimCompanyName: timesheet.travailleur.interimCompanyName,
        dayDetails: {
          lundi: timesheet.dayDetails.lundi,
          mardi: timesheet.dayDetails.mardi,
          mercredi: timesheet.dayDetails.mercredi,
          jeudi: timesheet.dayDetails.jeudi,
          vendredi: timesheet.dayDetails.vendredi,
          samedi: timesheet.dayDetails.samedi,
          dimanche: timesheet.dayDetails.dimanche
        }
      });

      // Transform online response to match the expected return type
      return {
        timesheet: response.data.timesheet || {
          id: response.data.timesheetId || timesheet.id,
          chefChantier: timesheet.chefChantier,
          chefProjet: timesheet.chefProjet,
          travailleur: timesheet.travailleur,
          weekNumber: timesheet.weekNumber,
          timesheetDate: timesheet.timesheetDate,
          foreman_validated: timesheet.foreman_validated,
          worksite: timesheet.worksite,
          hr_validated: timesheet.hr_validated,
          syncStatus: 'synced',
          remark: timesheet.remark,
          isInterim: timesheet.isInterim,
          interimCompanyName: timesheet.travailleur.interimCompanyName,
          created_at: response.data.updatedAt || timesheet.created_at || new Date().toISOString(),
          dayDetails: {
            lundi: timesheet.dayDetails.lundi,
            mardi: timesheet.dayDetails.mardi,
            mercredi: timesheet.dayDetails.mercredi,
            jeudi: timesheet.dayDetails.jeudi,
            vendredi: timesheet.dayDetails.vendredi,
            samedi: timesheet.dayDetails.samedi,
            dimanche: timesheet.dayDetails.dimanche
          }
        }
      };
    } catch (error) {
      return rejectWithValue('Erreur lros de la mise a jour: ' + error);
    }
  }
);

export const deleteTimesheet = createAsyncThunk<
  { id?: string | number; localId?: string; },
  Timesheet,
  {
    rejectValue: string;
  }
>(
  'timesheets/deleteTimesheet',
  async (timesheet: Timesheet, { rejectWithValue, dispatch }) => {
    try {
      // If it's just a local timesheet (not yet synced)
      if (!timesheet.id) {
        return { localId: timesheet.localId };
      }
      // For server-side timesheets
      const response = await removeTimesheet(timesheet.id);

      // Return a properly typed response for online deletion
      return {
        id: timesheet.id,
        success: response.data?.success || true
      };
    } catch (error: any) {
      return rejectWithValue('Erreur lors de la suppression');
    }
  }
);

// Update validate functions to refresh list after validation
export const validateTimesheetByForeman = createAsyncThunk(
  'timesheets/validateByForeman',
  async (timesheetId: string, { rejectWithValue, dispatch }) => {
    try {
      await validateByForeman(timesheetId);

      return timesheetId;
    } catch (error) {
      return rejectWithValue('Erreur de la validation par le conducteurt de travaux');
    }
  }
);

export const validateTimesheetByHR = createAsyncThunk(
  'timesheets/validateByHR',
  async (timesheetId: string, { rejectWithValue, dispatch }) => {
    try {
      await validateByHr(timesheetId);
      return timesheetId;
    } catch (error) {
      return rejectWithValue('Erreur de la validation RH');
    }
  }
);

// Update syncPendingTimesheets to refresh list after sync
export const syncPendingTimesheets = createAsyncThunk(
  'timesheets/syncPending',
  async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState() as RootState;
    const pendingItems = state.timesheets.pendingItems;

    // If no pending items, don't do anything
    if (pendingItems.length === 0) {
      return [];
    }

    const results = [];

    try {
      for (const timesheet of pendingItems) {
        try {
          // Handle items marked for deletion differently
          if (timesheet._pendingDelete) {
            if (timesheet.id) {
              // Delete from server
              const response = await removeTimesheet(timesheet.id);
              results.push({
                localId: timesheet.localId,
                success: true,
                isDelete: true,
                serverResponse: response.data
              });
            } else {
              // Item was created offline and then deleted offline, just mark as successful
              results.push({
                localId: timesheet.localId,
                success: true,
                isDelete: true
              });
            }
          } else {
            // Regular sync for non-deleted items
            const apiTimesheet: Timesheet = {
              chefChantier: timesheet.chefChantier,
              chefProjet: timesheet.chefProjet,
              travailleur: timesheet.travailleur,
              timesheetDate: timesheet.timesheetDate,
              weekNumber: timesheet.weekNumber,
              worksite: timesheet.worksite,
              remark: timesheet.remark,
              isInterim: timesheet.isInterim,
              interimCompanyName: timesheet.interimCompanyName,
              dayDetails: {
                lundi: timesheet.dayDetails.lundi,
                mardi: timesheet.dayDetails.mardi,
                mercredi: timesheet.dayDetails.mercredi,
                jeudi: timesheet.dayDetails.jeudi,
                vendredi: timesheet.dayDetails.vendredi,
                samedi: timesheet.dayDetails.samedi,
                dimanche: timesheet.dayDetails.dimanche
              }
            };

            // If it has an ID, it's an update, not a new creation
            if (timesheet._pendingUpdate) {
              apiTimesheet.id = timesheet.id;
              const response = await editTimesheet({
                id: timesheet.id,
                chefChantier: timesheet.chefChantier.name,
                chefProjet: timesheet.chefProjet.name,
                travailleur: timesheet.travailleur.name,
                weekNumber: timesheet.weekNumber,
                timesheetDate: timesheet.timesheetDate,
                worksite: timesheet.worksite.name,
                remark: timesheet.remark,
                isInterim: timesheet.isInterim,
                interimCompanyName: timesheet.interimCompanyName,
                dayDetails: {
                  lundi: timesheet.dayDetails.lundi,
                  mardi: timesheet.dayDetails.mardi,
                  mercredi: timesheet.dayDetails.mercredi,
                  jeudi: timesheet.dayDetails.jeudi,
                  vendredi: timesheet.dayDetails.vendredi,
                  samedi: timesheet.dayDetails.samedi,
                  dimanche: timesheet.dayDetails.dimanche
                }
              });
              results.push({
                localId: timesheet.localId,
                success: true,
                serverResponse: response.data
              });
            } else {
              const response = await saveTimesheet({
                chefChantier: timesheet.chefChantier.name,
                chefProjet: timesheet.chefProjet.name,
                travailleur: timesheet.travailleur.name,
                weekNumber: timesheet.weekNumber,
                timesheetDate: timesheet.timesheetDate,
                worksite: timesheet.worksite.name,
                remark: timesheet.remark,
                isInterim: timesheet.isInterim,
                interimCompanyName: timesheet.interimCompanyName,
                dayDetails: {
                  lundi: timesheet.dayDetails.lundi,
                  mardi: timesheet.dayDetails.mardi,
                  mercredi: timesheet.dayDetails.mercredi,
                  jeudi: timesheet.dayDetails.jeudi,
                  vendredi: timesheet.dayDetails.vendredi,
                  samedi: timesheet.dayDetails.samedi,
                  dimanche: timesheet.dayDetails.dimanche
                }
              });
              results.push({
                localId: timesheet.localId,
                success: true,
                serverResponse: response.data
              });
            }
          }
        } catch (error: any) {
          if (error.response.data.error.toLocaleLowerCase() === "not found") {

            // update the state of pending items to remove the item
            results.push({
              localId: timesheet.localId,
              success: true,
              isDelete: true,
              serverResponse: { message: "Item not found on server, removing from pending items" }
            });

          } else {
            return rejectWithValue('Failed sync data:' + error.response.data.error);
          }
        }
      }

      return results;
    } catch (error) {
      return rejectWithValue('Failed sync data');
    }

  }
);

export const purgeTimesheets = createAsyncThunk(
  'timesheets/purgeTimesheets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await purgeOldTimesheets();
      // Tu peux retourner un message ou un booléen
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erreur lors de la suppression des données');
    }
  }
);

const timesheetSlice = createSlice({
  name: 'timesheets',
  initialState,
  reducers: {
    addOfflineTimesheetOperation: (state, action: PayloadAction<Timesheet>) => {
      const timesheet: Timesheet = {
        ...action.payload,
        localId: action.payload.id || `local_${Date.now()}`,
        syncStatus: 'pending',
        created_at: new Date().toISOString(),
      };
      state.pendingItems.push(timesheet);
    },
    editOfflineTimesheetOperation: (state, action: PayloadAction<Timesheet>) => {
      const timesheetId = action.payload.localId || action.payload.id;
      const index = state.pendingItems.findIndex(item => item.localId === timesheetId || item.id === timesheetId);
      if (index !== -1) {
        state.pendingItems[index] = {
          ...state.pendingItems[index],
          ...action.payload,
          created_at: new Date().toISOString(),
        };
      } else {
        state.pendingItems.push(action.payload);
      }
    },
    clearSyncedTimesheets: (state) => {
      state.pendingItems = state.pendingItems.filter(
        (item) => item.syncStatus !== 'synced'
      );
    },
    removeOfflineTimesheetOperation: (state, action: PayloadAction<Timesheet>) => {
      const timesheetId = action.payload.localId || action.payload.id;
      const index = state.pendingItems.findIndex(item => item.localId === timesheetId || item.id === timesheetId);
      if (index !== -1) {
        state.pendingItems.splice(index, 1);
      } else {
        const timesheet: Timesheet = {
          ...action.payload,
          localId: action.payload.id || action.payload.localId || `local_${Date.now()}`,
          syncStatus: 'pending',
          created_at: new Date().toISOString(),
          _pendingDelete: true,
          _pendingUpdate: false
        };
        state.pendingItems.push(timesheet);
      }
    },
    reset: (state) => {
      state.items = [];
      state.pendingItems = [];
      state.loading = false;
      state.error = null;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchTimesheetsByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimesheetsByUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.items = action.payload;
        }
      })
      .addCase(fetchTimesheetsByUser.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload;
        if (action.payload?.currentItems) {
          state.items = action.payload.currentItems;
        }
      })
      .addCase(fetchTimesheetsByForeman.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimesheetsByForeman.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchTimesheetsByForeman.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload;
        if (action.payload?.currentItems) {
          state.items = action.payload.currentItems;
        }
      })
      .addCase(fetchTimesheetsByHR.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimesheetsByHR.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchTimesheetsByHR.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload;
        if (action.payload?.currentItems) {
          state.items = action.payload.currentItems;
        }
      })
      .addCase(fetchAllTimesheets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTimesheets.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchAllTimesheets.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload;
        if (action.payload?.currentItems) {
          state.items = action.payload.currentItems;
        }
      })
      .addCase(createTimesheet.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createTimesheet.rejected, (state, action: any) => {
        if (action.payload?.timesheet) {
          state.pendingItems.push({ ...action.payload.timesheet, syncStatus: 'pending', _pendingDelete: false, _pendingUpdate: false });
        }
      })
      .addCase(validateTimesheetByForeman.fulfilled, (state, action) => {
        const id = action.payload;
        const timesheet = state.items.find(item => item.id === id);
        if (timesheet) {
          timesheet.foreman_validated = true;
        }
      })
      .addCase(validateTimesheetByForeman.rejected, (state, action: any) => {
        state.error = action.payload?.error || action.payload;
        if (action.payload?.timesheet) {
          state.pendingItems.push({ ...action.payload.timesheet, syncStatus: 'pending', _pendingDelete: false, _pendingUpdate: true });
        }
      })
      .addCase(validateTimesheetByHR.fulfilled, (state, action) => {
        const id = action.payload;
        const timesheet = state.items.find(item => item.id === id);
        if (timesheet) {
          timesheet.hr_validated = true;
        }
      })
      .addCase(validateTimesheetByHR.rejected, (state, action: any) => {
        state.error = action.payload?.error || action.payload;
        if (action.payload?.timesheet) {
          state.pendingItems.push({ ...action.payload.timesheet, syncStatus: 'pending', _pendingDelete: false, _pendingUpdate: true });
        }
      })
      .addCase(syncPendingTimesheets.fulfilled, (state, action) => {
        const results = action.payload;

        // Update successfully synced items
        results.forEach(result => {
          if (result.success) {
            // Find and remove from pending items
            const index = state.pendingItems.findIndex(
              item => item.localId === result.localId || item.id === result.localId
            );

            if (index !== -1) {
              // If this was a deletion operation, just remove from pending items
              state.pendingItems.splice(index, 1);
            }
          } else {
            // Mark as failed sync
            const item = state.pendingItems.find(
              item => item.localId === result.localId || item.id === result.localId
            );
            if (item) {
              item.syncStatus = 'failed';
            }
          }
        });
      })
      .addCase(syncPendingTimesheets.rejected, (state, action: any) => {
        state.error = action.payload?.error || action.payload;
      })
      .addCase(updateTimesheet.fulfilled, (state, action) => {
        // Handle local update
        if (action.payload.timesheet.localId) {
          const index = state.pendingItems.findIndex(item => item.localId === action.payload.timesheet.localId);
          if (index !== -1) {
            state.pendingItems[index] = { ...action.payload.timesheet };
          }
        }
        state.loading = false;
      })
      .addCase(updateTimesheet.rejected, (state, action: any) => {
        state.error = action.payload.error || action.payload;

        if (action.payload?.timesheet) {
          state.pendingItems.push({ ...action.payload.timesheet, syncStatus: 'pending', _pendingDelete: true, _pendingUpdate: false });
        }
      })
      .addCase(deleteTimesheet.fulfilled, (state, action) => {
        // Handle local deletion
        if (action.payload.localId) {
          state.pendingItems = state.pendingItems.filter(
            item => item.localId !== action.payload.localId
          );
        }
        state.loading = false;
      })
      .addCase(deleteTimesheet.rejected, (state, action: any) => {
        state.error = action.payload?.error || action.payload;


        if (action.payload?.timesheet) {
          state.pendingItems.push({ ...action.payload.timesheet, syncStatus: 'pending', _pendingDelete: false, _pendingUpdate: true });
        }
      })
      .addCase(purgeTimesheets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(purgeTimesheets.fulfilled, (state, action) => {
        state.loading = false;
        state.items = [];
        const deletedCount = action.payload?.deletedCount ?? 0;
        toast.success(`Purge effectuée avec succès : ${deletedCount} entrées supprimées.`);
      })
      .addCase(purgeTimesheets.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || 'Erreur lors de la purge';
        toast.error(state.error);
      });
  }
});

export const { addOfflineTimesheetOperation, removeOfflineTimesheetOperation, editOfflineTimesheetOperation, clearSyncedTimesheets, reset } = timesheetSlice.actions;
export default timesheetSlice.reducer;
